import { useState, useEffect, useRef, useCallback } from 'react';

const SOCKET_URL = "wss://zzz3967bjl.execute-api.us-west-2.amazonaws.com/production";

export const useGameSocket = (gameType) => {
    const [status, setStatus] = useState('disconnected'); // disconnected, connecting, waiting, playing, error
    const [role, setRole] = useState(null); // 'p1' (host) or 'p2' (guest)
    const [gameId, setGameId] = useState(null);
    const socketRef = useRef(null);
    const handlersRef = useRef({}); // { [type]: callback }

    const hostGame = useCallback(() => {
        if (socketRef.current) socketRef.current.close();

        setStatus('connecting');
        const ws = new WebSocket(SOCKET_URL);
        socketRef.current = ws;

        ws.onopen = () => {
            console.log("WS Open (Host)");
            ws.send(JSON.stringify({ action: 'host', gameType }));
        };

        setupListeners(ws);
    }, [gameType]);

    const joinGame = useCallback((code) => {
        if (socketRef.current) socketRef.current.close();

        setStatus('connecting');
        const ws = new WebSocket(SOCKET_URL);
        socketRef.current = ws;

        ws.onopen = () => {
            console.log("WS Open (Join)");
            ws.send(JSON.stringify({ action: 'join', code }));
        };

        setupListeners(ws);
    }, [gameType]);

    const setupListeners = (ws) => {
        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                console.log("WS Message:", msg);

                if (msg.type === 'game_code') {
                    // We are hosting, received our code
                    setStatus('waiting_for_opponent');
                    setGameId(msg.code); // Display connection code
                } else if (msg.type === 'game_start') {
                    setStatus('playing');
                    // gameId is usually code or server format, update if needed
                    // msg.gameId might be set if server sends it

                    if (msg.role === 'host') setRole('p1');
                    else if (msg.role === 'guest') setRole('p2');
                    else setRole(msg.role);
                } else if (msg.type === 'relay') {
                    const handler = handlersRef.current[msg.data.type];
                    if (handler) handler(msg.data);
                    else if (handlersRef.current['*']) handlersRef.current['*'](msg.data);
                } else if (msg.type === 'opponent_left') {
                    setStatus('disconnected');
                    alert("Opponent Disconnected");
                } else if (msg.type === 'error') {
                    alert("Error: " + msg.message);
                    setStatus('disconnected');
                    ws.close();
                }
            } catch (e) {
                console.error("WS Parse Error", e);
            }
        };

        ws.onclose = () => {
            console.log("WS Close");
            setStatus('disconnected');
            socketRef.current = null;
        };

        ws.onerror = (e) => {
            console.error("WS Error", e);
            setStatus('error');
        };
    };

    // Remove legacy connect
    // return { status, role, gameId, hostGame, joinGame, sendAction, on };

    const sendAction = useCallback((data) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            console.log("WS Send:", data);
            socketRef.current.send(JSON.stringify({
                action: 'relay',
                data: data
            }));
        }
    }, []);

    const on = useCallback((type, handler) => {
        handlersRef.current[type] = handler;
    }, []);

    const disconnect = useCallback(() => {
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }
        setStatus('disconnected');
    }, []);

    useEffect(() => {
        return () => {
            if (socketRef.current) socketRef.current.close();
        };
    }, []);

    return { status, role, gameId, hostGame, joinGame, sendAction, on };
};
